import React, { useState, useEffect } from 'react';
import { TICKET_SITES, getSiteDisplayName } from '../utils/siteConstants';
import {
  IonCard,
  IonButton,
  IonIcon,
  IonSearchbar,
  IonFab,
  IonFabButton,
  IonModal,
  IonInput,
  IonToast,
  IonRefresher,
  IonRefresherContent,
  RefresherEventDetail,
  IonList,
  IonItem,
  IonLabel,
  IonSelect,
  IonSelectOption,
  IonActionSheet,
  IonAlert,
  IonHeader,
  IonToolbar,
  IonTitle,
  IonButtons,
  IonContent,
  IonSpinner,
} from '@ionic/react';
import {
  addOutline,
  ellipsisVertical,
  createOutline,
  trashOutline,
  linkOutline,
  copyOutline,
  logoWhatsapp,
  cashOutline,
} from 'ionicons/icons';
import { useTranslation } from 'react-i18next';
import { format } from 'date-fns';
import { ticketService } from '../services/api';
import { Ticket, Event } from '../types';
import AddTicketModal from './AddTicketModal';
import InventoryOverview from './InventoryOverview';
import InventoryFulfillmentModal from './InventoryFulfillmentModal';
import {
  getCurrencySymbol,
  DEFAULT_CURRENCY,
} from '../utils/currencyConstants';
import { eventBus, EVENTS } from '../utils/eventBus';
import { formatSplitDisplay } from '../utils/formatUtils';
import { generateTicketMessage } from '../utils/messageUtils';
import ConnectionDetailsModal from './ConnectionDetailsModal';
import AddSaleModal from './AddSaleModal';
import InlineEdit from './InlineEdit';
import { SPLIT_TYPE_OPTIONS } from '../utils/ticketConstants';
import SourceDetailsModal from './SourceDetailsModal';

interface InventoryContentProps {
  event: Event;
}

const InventoryContent: React.FC<InventoryContentProps> = ({ event }) => {
  const { t } = useTranslation();
  const [tickets, setTickets] = useState<Ticket[]>([]);
  const [filteredTickets, setFilteredTickets] = useState<Ticket[]>([]);
  const [loading, setLoading] = useState(true);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [selectedTicket, setSelectedTicket] = useState<Ticket | null>(null);
  const [showToast, setShowToast] = useState(false);
  const [toastMessage, setToastMessage] = useState('');
  const [searchText, setSearchText] = useState('');
  const [showActionSheet, setShowActionSheet] = useState(false);
  const [showDeleteAlert, setShowDeleteAlert] = useState(false);
  const [ticketToDelete, setTicketToDelete] = useState<Ticket | null>(null);
  const [showFulfillmentModal, setShowFulfillmentModal] = useState(false);
  const [showConnectionDetails, setShowConnectionDetails] = useState(false);
  const [connectionItemId, setConnectionItemId] = useState<string | null>(null);
  const [showSaleModal, setShowSaleModal] = useState(false);
  const [inventoryForSale, setInventoryForSale] = useState<Ticket | null>(null);
  const [showSourceDetailsModal, setShowSourceDetailsModal] = useState(false);
  const [selectedSourceData, setSelectedSourceData] = useState<{
    type: 'supplier' | 'buyer' | 'site' | 'private';
    data: any;
  } | null>(null);

  // Remove editFormData state as we'll use AddTicketModal for editing

  useEffect(() => {
    loadTickets();
  }, [event]);

  const handleTicketRowClick = (ticket: Ticket) => {
    const ticketId = ticket.id || ticket._id;
    if (ticketId) {
      setConnectionItemId(ticketId);
      setShowConnectionDetails(true);
    }
  };

  const handleInventorySourceClick = (ticket: Ticket, event: React.MouseEvent) => {
    event.stopPropagation(); // Prevent row click from triggering
    
    // Determine source type and data
    if (ticket.sourceType === 'supplier' && ticket.supplierId) {
      // Supplier inventory
      setSelectedSourceData({
        type: 'supplier',
        data: ticket.supplierId
      });
    } else if (ticket.sourceType === 'site') {
      // Site/platform inventory
      setSelectedSourceData({
        type: 'site',
        data: ticket
      });
    } else {
      // Private inventory
      setSelectedSourceData({
        type: 'private',
        data: ticket
      });
    }
    
    setShowSourceDetailsModal(true);
  };

  useEffect(() => {
    filterTicketsList();
  }, [tickets, searchText]);

  useEffect(() => {
    // Subscribe to event bus for refreshing tickets
    const unsubscribe = eventBus.on(EVENTS.REFRESH_TICKETS, () => {
      console.log(
        'InventoryContent: Refreshing tickets due to sale from inventory'
      );
      loadTickets();
    });

    // Cleanup subscription on unmount
    return () => {
      unsubscribe();
    };
  }, []);

  const loadTickets = async () => {
    try {
      setLoading(true);
      const data = await ticketService.getAll({ eventId: event._id });
      setTickets(data || []);
    } catch (err: any) {
      setToastMessage(err.message || t('errors.generic'));
      setShowToast(true);
    } finally {
      setLoading(false);
    }
  };

  const filterTicketsList = () => {
    let filtered = [...tickets];

    if (searchText) {
      filtered = filtered.filter(
        (ticket) =>
          ticket.category?.toLowerCase().includes(searchText.toLowerCase()) ||
          ticket.block?.toLowerCase().includes(searchText.toLowerCase()) ||
          ticket.section?.toLowerCase().includes(searchText.toLowerCase()) ||
          ticket.supplier?.toLowerCase().includes(searchText.toLowerCase()) ||
          ticket.row?.toLowerCase().includes(searchText.toLowerCase()) ||
          ticket.seat?.toLowerCase().includes(searchText.toLowerCase()) ||
          ticket.split?.toLowerCase().includes(searchText.toLowerCase())
      );
    }

    setFilteredTickets(filtered);
  };

  const handleRefresh = async (event: CustomEvent<RefresherEventDetail>) => {
    await loadTickets();
    event.detail.complete();
  };

  const handleAddTicketSuccess = () => {
    setToastMessage(t('inventory.addTicketSuccess'));
    setShowToast(true);
    loadTickets();
  };

  const handleEditTicketSuccess = () => {
    setToastMessage(t('inventory.updateTicketSuccess'));
    setShowToast(true);
    setShowEditModal(false);
    resetEditForm();
    loadTickets();
  };

  const handleDeleteTicket = async () => {
    if (!ticketToDelete) return;

    try {
      const ticketId = ticketToDelete.id || ticketToDelete._id;
      if (!ticketId) {
        throw new Error('Ticket ID not found');
      }
      await ticketService.delete(ticketId);
      setToastMessage(t('inventory.deleteTicketSuccess'));
      setShowToast(true);
      setShowDeleteAlert(false);
      setTicketToDelete(null);
      loadTickets();
    } catch (err: any) {
      setToastMessage(err.message || t('errors.generic'));
      setShowToast(true);
    }
  };

  const resetEditForm = () => {
    setSelectedTicket(null);
  };

  const openEditModal = (ticket: Ticket) => {
    setSelectedTicket(ticket);
    setShowEditModal(true);
    setShowActionSheet(false);
  };

  const openDeleteAlert = (ticket: Ticket) => {
    setTicketToDelete(ticket);
    setShowDeleteAlert(true);
    setShowActionSheet(false);
  };

  const generateTicketMessageForCopy = (ticket: Ticket) => {
    const eventObj = ticket.eventId;
    const eventName = typeof eventObj === 'object' && eventObj ? eventObj.name : ticket.eventName || 'Event';

    return generateTicketMessage({
      eventName,
      category: ticket.category,
      block: ticket.block,
      split: ticket.split,
      quantity: ticket.quantity,
      soldQuantity: ticket.soldQuantity,
      wantedPrice: ticket.wantedPrice,
      currency: ticket.currency,
    });
  };

  const copyTicketDetails = async (ticket: Ticket) => {
    const availableQty = ticket.quantity - (ticket.soldQuantity || 0);

    if (availableQty <= 0) {
      setToastMessage('No tickets available to copy');
      setShowToast(true);
      return;
    }

    const message = generateTicketMessageForCopy(ticket);

    try {
      await navigator.clipboard.writeText(message);
      setToastMessage('Ticket details copied to clipboard!');
      setShowToast(true);
    } catch (error) {
      setToastMessage('Failed to copy to clipboard');
      setShowToast(true);
    }
    setShowActionSheet(false);
  };

  const sendViaWhatsApp = (ticket: Ticket) => {
    const availableQty = ticket.quantity - (ticket.soldQuantity || 0);

    if (availableQty <= 0) {
      setToastMessage('No tickets available to share');
      setShowToast(true);
      return;
    }

    const message = generateTicketMessageForCopy(ticket);
    const encodedMessage = encodeURIComponent(message);

    // Open WhatsApp with pre-filled message
    // Works on both mobile (opens app) and desktop (opens WhatsApp Web)
    window.open(`https://wa.me/?text=${encodedMessage}`, '_blank');

    setShowActionSheet(false);
  };

  const handleInlineInventoryEdit = async (ticketId: string, field: string, value: string | number) => {
    try {
      let processedValue = value;
      
      // Round price fields to 2 decimal places
      if (field === 'purchasePrice' || field === 'wantedPrice') {
        processedValue = Math.round(Number(value) * 100) / 100;
      }
      
      const updateData: any = { [field]: processedValue };
      
      await ticketService.update(ticketId, updateData);
      setToastMessage(`Ticket ${field} updated successfully`);
      setShowToast(true);
      await loadTickets();
    } catch (err: any) {
      setToastMessage(err.message || `Failed to update ${field}`);
      setShowToast(true);
      await loadTickets();
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'available':
        return 'success';
      case 'sold':
        return 'danger';
      case 'reserved':
        return 'warning';
      default:
        return 'medium';
    }
  };

  return (
    <div>
      <IonRefresher slot="fixed" onIonRefresh={handleRefresh}>
        <IonRefresherContent></IonRefresherContent>
      </IonRefresher>

      <div className="px-4 mb-4">
        <InventoryOverview tickets={tickets} loading={loading} />
      </div>

      <div className="px-4 mb-4">
        <IonSearchbar
          value={searchText}
          onIonChange={(e) => setSearchText(e.detail.value!)}
          placeholder={t('inventory.searchTickets')}
        />
      </div>

      <div className="px-4">
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <IonSpinner name="crescent" className="w-8 h-8" color="primary" />
          </div>
        ) : filteredTickets.length === 0 ? (
          <div className="text-center py-12">
            <p className="text-gray-500 dark:text-gray-400">
              {searchText
                ? t('common.noResultsFound')
                : t('inventory.noTickets')}
            </p>
          </div>
        ) : (
          <IonCard className="shadow-lg">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="bg-gray-50 dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700">
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                      {t('inventory.category')}
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                      {t('inventory.block')}
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                      Split
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                      Source
                    </th>
                    <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                      Total Tickets
                    </th>
                    <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                      Tickets Sold
                    </th>
                    <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                      {t('inventory.purchasePrice')}
                    </th>
                    <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                      Wanted Price
                    </th>
                    <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                      {t('inventory.status')}
                    </th>
                    <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                      {t('common.actions')}
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white dark:bg-gray-900 divide-y divide-gray-200 dark:divide-gray-700">
                  {filteredTickets.map((ticket, index) => (
                    <tr
                      key={ticket.id || ticket._id || index}
                      className="hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors cursor-pointer"
                      onClick={() => handleTicketRowClick(ticket)}
                      title="Click to view connection details"
                    >
                      <td className="px-4 py-3 whitespace-nowrap">
                        <div className="text-sm text-gray-900 dark:text-gray-100">
                          {ticket.category || '-'}
                        </div>
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap">
                        <div className="text-sm text-gray-900 dark:text-gray-100">
                          {ticket.block || '-'}
                        </div>
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap" onClick={(e) => e.stopPropagation()}>
                        <div className="text-sm text-gray-900 dark:text-gray-100">
                          <InlineEdit
                            type="select"
                            value={ticket.split || 'together'}
                            options={SPLIT_TYPE_OPTIONS}
                            onSave={(value) => {
                              const ticketId = ticket.id || ticket._id;
                              if (ticketId) handleInlineInventoryEdit(ticketId, 'split', value);
                            }}
                            selectLabel="Select split type"
                          />
                        </div>
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap">
                        <div className="text-sm text-gray-900 dark:text-gray-100">
                          {(() => {
                            // Determine source display
                            if (ticket.sourceType === 'site') {
                              const supplierName = ticket.supplier || 'Unknown Site';
                              const matchingSite = TICKET_SITES.find(
                                site => site.displayName.toLowerCase() === supplierName.toLowerCase()
                              );
                              const displayName = matchingSite ? matchingSite.displayName : supplierName;
                              return (
                                <span 
                                  className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-200 cursor-pointer hover:opacity-80 transition-opacity"
                                  onClick={(e) => handleInventorySourceClick(ticket, e)}
                                  title="Click to view source details"
                                >
                                  {displayName}
                                </span>
                              );
                            }
                            
                            if (ticket.sourceType === 'supplier') {
                              let supplierName = 'Unknown Supplier';
                              
                              // Check if supplierId is populated object (highest priority)
                              if (typeof ticket.supplierId === 'object' && ticket.supplierId !== null && ticket.supplierId?.name) {
                                supplierName = ticket.supplierId.name;
                              } 
                              // Fallback to supplier string field
                              else if (ticket.supplier && ticket.supplier.trim()) {
                                supplierName = ticket.supplier;
                              }
                              
                              return (
                                <span 
                                  className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200 cursor-pointer hover:opacity-80 transition-opacity"
                                  onClick={(e) => handleInventorySourceClick(ticket, e)}
                                  title="Click to view source details"
                                >
                                  {supplierName}
                                </span>
                              );
                            }
                            
                            // Private or no sourceType - handle private name
                            let privateName = 'Private';
                            if (ticket.supplier && ticket.supplier.trim()) {
                              privateName = ticket.supplier;
                            }
                            
                            return (
                              <span 
                                className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-200 cursor-pointer hover:opacity-80 transition-opacity"
                                onClick={(e) => handleInventorySourceClick(ticket, e)}
                                title="Click to view source details"
                              >
                                {privateName}
                              </span>
                            );
                          })()}
                        </div>
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-center" onClick={(e) => e.stopPropagation()}>
                        <div className="text-sm font-medium text-gray-900 dark:text-gray-100">
                          <InlineEdit
                            type="number"
                            value={ticket.quantity || 1}
                            onSave={(value) => {
                              const ticketId = ticket.id || ticket._id;
                              if (ticketId) handleInlineInventoryEdit(ticketId, 'quantity', Number(value));
                            }}
                            placeholder="Quantity"
                          />
                        </div>
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-center">
                        <div className="text-sm font-medium text-gray-900 dark:text-gray-100">
                          {ticket.soldQuantity || 0}
                          {(ticket.soldQuantity || 0) > 0 && (
                            <div className="text-xs text-gray-500">
                              ({ticket.quantity - (ticket.soldQuantity || 0)}{' '}
                              available)
                            </div>
                          )}
                        </div>
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-right" onClick={(e) => e.stopPropagation()}>
                        <div className="text-sm font-medium text-gray-900 dark:text-gray-100">
                          {getCurrencySymbol(
                            ticket.currency || DEFAULT_CURRENCY
                          )}
                          <InlineEdit
                            type="number"
                            value={ticket.purchasePrice || 0}
                            onSave={(value) => {
                              const ticketId = ticket.id || ticket._id;
                              if (ticketId) handleInlineInventoryEdit(ticketId, 'purchasePrice', Number(value));
                            }}
                            placeholder="Price"
                            isPrice={true}
                          />
                        </div>
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-right" onClick={(e) => e.stopPropagation()}>
                        <div className="text-sm font-medium text-gray-900 dark:text-gray-100">
                          {getCurrencySymbol(ticket.currency || DEFAULT_CURRENCY)}
                          <InlineEdit
                            type="number"
                            value={ticket.wantedPrice || 0}
                            onSave={(value) => {
                              const ticketId = ticket.id || ticket._id;
                              if (ticketId) handleInlineInventoryEdit(ticketId, 'wantedPrice', Number(value));
                            }}
                            placeholder="Wanted price"
                            isPrice={true}
                          />
                        </div>
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-center">
                        <span
                          className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                            ticket.status === 'available'
                              ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200'
                              : ticket.status === 'sold'
                              ? 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200'
                              : 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200'
                          }`}
                        >
                          {t(`inventory.${ticket.status}`)}
                        </span>
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-center" onClick={(e) => e.stopPropagation()}>
                        <div className="flex justify-center gap-1">
                          <IonButton
                            fill="clear"
                            size="small"
                            onClick={(e) => {
                              e.stopPropagation();
                              copyTicketDetails(ticket);
                            }}
                            title="Copy details"
                          >
                            <IonIcon icon={copyOutline} />
                          </IonButton>
                          <IonButton
                            fill="clear"
                            size="small"
                            onClick={(e) => {
                              e.stopPropagation();
                              sendViaWhatsApp(ticket);
                            }}
                            title="Share via WhatsApp"
                            className="text-green-600"
                          >
                            <IonIcon icon={logoWhatsapp} />
                          </IonButton>
                          <IonButton
                            fill="clear"
                            size="small"
                            onClick={(e) => {
                              e.stopPropagation();
                              setSelectedTicket(ticket);
                              setShowActionSheet(true);
                            }}
                          >
                            <IonIcon icon={ellipsisVertical} />
                          </IonButton>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </IonCard>
        )}
      </div>

      <IonFab vertical="bottom" horizontal="end" slot="fixed">
        <IonFabButton onClick={() => setShowAddModal(true)}>
          <IonIcon icon={addOutline} />
        </IonFabButton>
      </IonFab>

      {/* Add Ticket Modal */}
      <AddTicketModal
        isOpen={showAddModal}
        onDismiss={() => setShowAddModal(false)}
        event={event}
        onSuccess={handleAddTicketSuccess}
      />

      {/* Edit Ticket Modal */}
      <AddTicketModal
        isOpen={showEditModal}
        onDismiss={() => {
          setShowEditModal(false);
          resetEditForm();
        }}
        event={event}
        ticket={selectedTicket}
        onSuccess={handleEditTicketSuccess}
      />

      {/* Action Sheet */}
      <IonActionSheet
        isOpen={showActionSheet}
        onDidDismiss={() => {
          setShowActionSheet(false);
          // Don't clear selectedTicket here as we need it for the modal
        }}
        buttons={[
          {
            text: 'Sell from Inventory',
            icon: cashOutline,
            handler: () => {
              if (selectedTicket) {
                const availableQty = selectedTicket.quantity - (selectedTicket.soldQuantity || 0);
                if (availableQty <= 0) {
                  setToastMessage('No tickets available to sell');
                  setShowToast(true);
                  return true;
                }
                setInventoryForSale(selectedTicket);
                setShowActionSheet(false);
                setTimeout(() => {
                  setShowSaleModal(true);
                }, 300);
              }
              return true;
            },
          },
          {
            text: 'Link to Sales',
            icon: linkOutline,
            handler: () => {
              setShowActionSheet(false);
              setTimeout(() => {
                setShowFulfillmentModal(true);
              }, 300);
              return true;
            },
          },
          {
            text: t('common.edit'),
            icon: createOutline,
            handler: () => {
              if (selectedTicket) openEditModal(selectedTicket);
            },
          },
          {
            text: t('common.delete'),
            icon: trashOutline,
            role: 'destructive',
            handler: () => {
              if (selectedTicket) openDeleteAlert(selectedTicket);
            },
          },
          {
            text: t('common.cancel'),
            role: 'cancel',
          },
        ]}
      />

      {/* Delete Alert */}
      <IonAlert
        isOpen={showDeleteAlert}
        onDidDismiss={() => setShowDeleteAlert(false)}
        header={t('common.confirm')}
        message={t('inventory.deleteTicketConfirm')}
        buttons={[
          {
            text: t('common.cancel'),
            role: 'cancel',
          },
          {
            text: t('common.delete'),
            handler: handleDeleteTicket,
          },
        ]}
      />

      {/* Toast */}
      <IonToast
        isOpen={showToast}
        onDidDismiss={() => setShowToast(false)}
        message={toastMessage}
        duration={3000}
      />

      {/* Fulfillment Modal */}
      <InventoryFulfillmentModal
        isOpen={showFulfillmentModal}
        onDismiss={() => {
          setShowFulfillmentModal(false);
          setSelectedTicket(null);
        }}
        ticket={selectedTicket}
        onSuccess={async () => {
          setToastMessage('Successfully linked inventory to sales');
          setShowToast(true);
          setShowFulfillmentModal(false);
          setSelectedTicket(null);
          await loadTickets();
        }}
      />

      {/* Connection Details Modal */}
      <ConnectionDetailsModal
        isOpen={showConnectionDetails}
        onDismiss={() => {
          setShowConnectionDetails(false);
          setConnectionItemId(null);
        }}
        type="inventory"
        itemId={connectionItemId}
      />

      {/* Add Sale Modal for selling from inventory */}
      <AddSaleModal
        isOpen={showSaleModal}
        onDismiss={() => {
          setShowSaleModal(false);
          setInventoryForSale(null);
          setSelectedTicket(null);
        }}
        event={event}
        inventoryItem={inventoryForSale}
        onSuccess={async () => {
          setToastMessage('Sale created successfully');
          setShowToast(true);
          setShowSaleModal(false);
          setInventoryForSale(null);
          setSelectedTicket(null);
          // Refresh inventory to show updated quantities
          await loadTickets();
        }}
      />

      {/* Source Details Modal */}
      <SourceDetailsModal
        isOpen={showSourceDetailsModal}
        onClose={() => {
          setShowSourceDetailsModal(false);
          setSelectedSourceData(null);
        }}
        sourceType={selectedSourceData?.type || 'private'}
        sourceData={selectedSourceData?.data || null}
        context="inventory"
      />
    </div>
  );
};

export default InventoryContent;
